import type { DiscountsData } from "@/types/billing";
import { billing_DiscountRequest } from "@/api/models/billing_DiscountRequest";

/**
 * Get human-readable weekday name from day number (1=Monday, 7=Sunday)
 */
function getWeekdayName(dayNumber: number): string {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return days[dayNumber - 1] || `Day ${dayNumber}`;
}

/**
 * Convert DiscountsData from the UI into API discount requests.
 * Weekday amounts are already expressed in minor currency units (rappen).
 */
export function discountsDataToRequests(
  discounts?: DiscountsData
): Array<billing_DiscountRequest> {
  if (!discounts) return [];

  const requests: Array<billing_DiscountRequest> = [];

  // Weekday discounts
  if (discounts.weekday) {
    Object.entries(discounts.weekday).forEach(([day, amount]) => {
      if (!amount || amount <= 0) return;
      const dayNumber = Number(day);
      if (!Number.isFinite(dayNumber) || dayNumber < 1 || dayNumber > 7) return;

      requests.push({
        category: billing_DiscountRequest.category.WEEKDAY,
        weekday: dayNumber,
        type: billing_DiscountRequest.type.FIXED,
        value: Math.round(amount),
        reason: `Weekday discount - ${getWeekdayName(dayNumber)}`,
      });
    });
  }

  // Custom discounts
  if (discounts.custom) {
    discounts.custom.forEach((discount) => {
      if (!discount || !discount.name) return;
      // Drop empty/zero values. Percentages must be positive; an "amount"
      // discount may be NEGATIVE — that is a surcharge that adds to the parent
      // total, so we keep negative amount values and let the backend apply them.
      if (!discount.value) return;
      if (discount.type === 'percentage' && discount.value <= 0) return;

      requests.push({
        category: billing_DiscountRequest.category.CUSTOM,
        type: discount.type === "percentage"
          ? billing_DiscountRequest.type.PERCENTAGE
          : billing_DiscountRequest.type.FIXED,
        value: discount.type === "percentage"
          ? discount.value
          : Math.round(discount.value),
        reason: discount.name,
      });
    });
  }

  return requests;
}
